import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { adminApi, Tenant } from '../lib/api'
import {
  Building2,
  TrendingUp,
  Plus,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  Clock,
} from 'lucide-react'

export default function Dashboard() {
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [loading, setLoading] = useState(true)
  const [healthStatus, setHealthStatus] = useState<'healthy' | 'unhealthy' | 'checking'>('checking')

  useEffect(() => {
    fetchData()
  }, [])

  async function fetchData() {
    setLoading(true)
    try {
      const [tenantsRes, healthRes] = await Promise.all([
        adminApi.getTenants(),
        adminApi.getHealth(),
      ])

      if (tenantsRes.success) {
        setTenants(tenantsRes.tenants)
      }

      setHealthStatus(healthRes.status === 'healthy' ? 'healthy' : 'unhealthy')
    } catch (err) {
      console.error('Failed to load data:', err)
      setHealthStatus('unhealthy')
    } finally {
      setLoading(false)
    }
  }

  const activeTenants = tenants.filter((t) => t.status === 'active').length
  const provisioningTenants = tenants.filter((t) => t.status === 'provisioning').length
  const suspendedTenants = tenants.filter((t) => t.status === 'suspended').length

  const tierCounts = {
    free: tenants.filter((t) => t.tier === 'free').length,
    pro: tenants.filter((t) => t.tier === 'pro').length,
    enterprise: tenants.filter((t) => t.tier === 'enterprise').length,
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-gray-400">Platform overview and management</p>
        </div>
        <Link to="/tenants/new" className="btn-primary">
          <Plus className="h-4 w-4 mr-2" />
          New Tenant
        </Link>
      </div>

      {/* System Status */}
      <div className="card p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className={`h-3 w-3 rounded-full ${
                healthStatus === 'healthy'
                  ? 'bg-green-500'
                  : healthStatus === 'unhealthy'
                  ? 'bg-red-500'
                  : 'bg-yellow-500 animate-pulse'
              }`}
            />
            <span className="text-sm font-medium text-gray-300">
              System Status:{' '}
              <span
                className={
                  healthStatus === 'healthy'
                    ? 'text-green-400'
                    : healthStatus === 'unhealthy'
                    ? 'text-red-400'
                    : 'text-yellow-400'
                }
              >
                {healthStatus === 'healthy'
                  ? 'All systems operational'
                  : healthStatus === 'unhealthy'
                  ? 'Issues detected'
                  : 'Checking...'}
              </span>
            </span>
          </div>
          <span className="text-xs text-gray-500">
            Last checked: {new Date().toLocaleTimeString()}
          </span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-400">Total Tenants</p>
              <p className="text-3xl font-bold text-white">{tenants.length}</p>
            </div>
            <div className="h-12 w-12 bg-primary-600/20 rounded-lg flex items-center justify-center">
              <Building2 className="h-6 w-6 text-primary-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp className="h-4 w-4 text-green-400 mr-1" />
            <span className="text-green-400">{activeTenants} active</span>
            {suspendedTenants > 0 && (
              <span className="text-red-400 ml-3">{suspendedTenants} suspended</span>
            )}
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-400">By Tier</p>
              <div className="flex items-baseline gap-4 mt-1">
                <div>
                  <span className="text-2xl font-bold text-white">{tierCounts.enterprise}</span>
                  <span className="text-sm text-gray-400 ml-1">Enterprise</span>
                </div>
                <div>
                  <span className="text-2xl font-bold text-white">{tierCounts.pro}</span>
                  <span className="text-sm text-gray-400 ml-1">Pro</span>
                </div>
                <div>
                  <span className="text-2xl font-bold text-white">{tierCounts.free}</span>
                  <span className="text-sm text-gray-400 ml-1">Free</span>
                </div>
              </div>
            </div>
            <div className="h-12 w-12 bg-blue-600/20 rounded-lg flex items-center justify-center">
              <TrendingUp className="h-6 w-6 text-blue-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Tenant Status Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <div className="p-6 border-b border-gray-700">
            <h2 className="text-lg font-semibold text-white">Tenant Status</h2>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span className="text-gray-300">Active</span>
              </div>
              <span className="text-white font-semibold">{activeTenants}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-yellow-400" />
                <span className="text-gray-300">Provisioning</span>
              </div>
              <span className="text-white font-semibold">{provisioningTenants}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-red-400" />
                <span className="text-gray-300">Suspended</span>
              </div>
              <span className="text-white font-semibold">{suspendedTenants}</span>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="p-6 border-b border-gray-700">
            <h2 className="text-lg font-semibold text-white">Subscription Tiers</h2>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-300">Free</span>
              <div className="flex items-center gap-3">
                <div className="w-32 h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gray-500 rounded-full"
                    style={{
                      width: `${tenants.length ? (tierCounts.free / tenants.length) * 100 : 0}%`,
                    }}
                  />
                </div>
                <span className="text-white font-semibold w-8 text-right">{tierCounts.free}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-300">Pro</span>
              <div className="flex items-center gap-3">
                <div className="w-32 h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded-full"
                    style={{
                      width: `${tenants.length ? (tierCounts.pro / tenants.length) * 100 : 0}%`,
                    }}
                  />
                </div>
                <span className="text-white font-semibold w-8 text-right">{tierCounts.pro}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-300">Enterprise</span>
              <div className="flex items-center gap-3">
                <div className="w-32 h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary-500 rounded-full"
                    style={{
                      width: `${tenants.length ? (tierCounts.enterprise / tenants.length) * 100 : 0}%`,
                    }}
                  />
                </div>
                <span className="text-white font-semibold w-8 text-right">{tierCounts.enterprise}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Tenants */}
      <div className="card">
        <div className="p-6 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Recent Tenants</h2>
          <Link
            to="/tenants"
            className="text-sm text-primary-400 hover:text-primary-300 flex items-center"
          >
            View all
            <ArrowRight className="h-4 w-4 ml-1" />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-800/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Tier
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Created
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {tenants.slice(0, 5).map((tenant) => (
                <tr key={tenant.id} className="hover:bg-gray-800/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Link
                      to={`/tenants/${tenant.id}`}
                      className="text-white hover:text-primary-400"
                    >
                      {tenant.company_name}
                    </Link>
                    <p className="text-sm text-gray-500">{tenant.slug}</p>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`badge-${
                        tenant.status === 'active'
                          ? 'green'
                          : tenant.status === 'provisioning'
                          ? 'yellow'
                          : tenant.status === 'suspended'
                          ? 'red'
                          : 'gray'
                      }`}
                    >
                      {tenant.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`badge-${
                        tenant.tier === 'enterprise'
                          ? 'purple'
                          : tenant.tier === 'pro'
                          ? 'blue'
                          : 'gray'
                      }`}
                    >
                      {tenant.tier}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                    {new Date(tenant.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
              {tenants.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center text-gray-500">
                    No tenants yet. Create your first tenant to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
